/**
 * Client-local recency store for the pipeline chip switcher (SE-5489).
 *
 * There is NO server `lastSelectedAt` / mark-recent mutation for kanban
 * boards, so "most recent pipelines" is tracked entirely client-side: an
 * Apollo reactive variable (most-recent-first list of board ids) backed by
 * AsyncStorage for cross-session persistence.
 *
 * Cross-device sync is OUT OF SCOPE (SE-6857 decision, not an oversight): the
 * `KanbanBoard` type carries no `lastSelectedAt` and the schema exposes no
 * mark-recently-selected mutation, so pipeline recency is deliberately
 * implemented as local behaviour rather than by adding a backend field.
 *
 * Bump policy (SE-6857): recency is bumped on EVERY path that opens a
 * pipeline — a chip tap, a pick from the all-pipelines drawer, a `?boardId=`
 * deep link, and a board restored or created at the screen root. SE-5489
 * originally bumped only on a drawer pick, so a pipeline opened any other way
 * never registered and the chip row could not report "the last 3 opened". The
 * chip row now reads this list live (no snapshot) and caps it at 3, so a bump
 * reorders the visible chips — accepted as the cost of chips that are actually
 * recency-ordered.
 * @module features/player-kanban/stores/pipelineRecencyStore
 */

import AsyncStorage from "@react-native-async-storage/async-storage";
import { makeVar } from "@apollo/client";

/**
 * AsyncStorage key for the persisted recent-pipeline id list. Namespaced to
 * the feature so it never collides with the watchlist `@scouting_*` keys.
 */
export const RECENT_PIPELINES_STORAGE_KEY = "@player_kanban_recent_pipelines";

/**
 * Upper bound on how many recent ids are retained in the store. Larger than
 * the chip cap (3) so that when a currently-visible chip is not re-picked it
 * still has history behind it, but bounded so the persisted blob stays small.
 */
export const RECENT_PIPELINES_MAX = 12;

/**
 * Reactive variable holding recently-selected pipeline board ids, most recent
 * first. Read reactively via {@link usePipelineRecency}; mutated only through
 * {@link bumpRecentPipeline} (any path that opens a pipeline) and
 * {@link hydrateRecentPipelines} (session start).
 */
export const recentPipelineBoardIdsVar = makeVar<readonly string[]>([]);

/**
 * Dedupe-prepend `boardId` to the recency list and persist. Called on every
 * path that opens a pipeline (see the bump policy above). If the id is already
 * present it is moved to the front (most recent) rather than duplicated.
 * Persistence is fire-and-forget — a storage failure must never block the
 * selection that already switched the active board.
 *
 * SE-6857: bumping an id that is ALREADY the most recent is a no-op. Several
 * open paths converge on the same board (a chip tap bumps directly, and the
 * chip row also bumps when it observes the active board change), and without
 * this guard each of them would write an equal-but-new array — which
 * `makeVar` compares by reference, so every redundant bump would broadcast a
 * re-render and burn an AsyncStorage write.
 * @param boardId - The freshly-opened pipeline board id.
 */
export const bumpRecentPipeline = (boardId: string): void => {
  if (!boardId) return;
  if (recentPipelineBoardIdsVar()[0] === boardId) return;
  const next = [
    boardId,
    ...recentPipelineBoardIdsVar().filter(id => id !== boardId),
  ].slice(0, RECENT_PIPELINES_MAX);
  recentPipelineBoardIdsVar(next);
  void AsyncStorage.setItem(
    RECENT_PIPELINES_STORAGE_KEY,
    JSON.stringify(next)
  ).catch(() => {
    // Swallowed: recency persistence is best-effort. The in-memory reactive
    // var still reflects the bump for the rest of the session.
  });
};

/**
 * Rehydrate the recency list from AsyncStorage into the reactive var. Called
 * once at session start (via {@link usePipelineRecency}). Ignores malformed or
 * missing payloads, leaving the var at its empty default. Does not overwrite a
 * list that already has in-session entries (a bump that raced ahead of
 * hydration wins over stale disk state).
 */
export const hydrateRecentPipelines = async (): Promise<void> => {
  try {
    const raw = await AsyncStorage.getItem(RECENT_PIPELINES_STORAGE_KEY);
    if (!raw) return;
    if (recentPipelineBoardIdsVar().length > 0) return;
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every((entry): entry is string => typeof entry === "string")
    ) {
      recentPipelineBoardIdsVar(parsed.slice(0, RECENT_PIPELINES_MAX));
    }
  } catch {
    // Swallowed: a corrupt/unavailable store leaves the empty default, which
    // renders a clean chip row rather than crashing navigation.
  }
};

/**
 * Test-only helper. Resets the recency var to empty so suites can exercise the
 * store in isolation without leaking state across cases.
 */
export const __resetRecentPipelinesForTests = (): void => {
  recentPipelineBoardIdsVar([]);
};
